// SupportChatShell.jsx
// Widget de chat assistant Elakay, extrait de la page userMessage.js pour
// pouvoir être réutilisé ailleurs (ex: modal "Contact Us" du footer) avec
// exactement le même comportement (historique, suggestions, Gemini, prise de
// contact avec un responsable...).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    FaArrowLeft,
    FaBriefcase,
    FaCrown,
    FaEllipsisH,
    FaExclamationCircle,
    FaExternalLinkAlt,
    FaHeadset,
    FaHistory,
    FaInfoCircle,
    FaLightbulb,
    FaPaperPlane,
    FaPlayCircle,
    FaPlus,
    FaQuestionCircle,
    FaRobot,
    FaSearchDollar,
    FaShieldAlt,
    FaShippingFast,
    FaStore,
    FaSyncAlt,
    FaTimes,
    FaTrash,
    FaUser,
    FaUserShield,
    FaUsers
} from 'react-icons/fa';
import Modal from 'react-bootstrap/Modal';
import { getElakayLang, isAuthenticated } from "../auth";
import {
    askSupportAssistant,
    deleteAllSupportChatHistories,
    deleteSupportChatHistory,
    getSupportChatHistories,
    getSupportResponsibles,
    restoreSupportChatHistory,
    saveSupportChatHistory
} from "../auth/apiMessage";
import { listChatbotIntents, trackChatbotIntentUsage } from "../auth/apiChatbotIntent";
import { createContactRequest, listMyContactRequests } from "../auth/apiContactRequest";
import { elakayAutoText } from "./dataElakay";
import {
    isContactRequest,
    knowledgeById,
    matchSupportTopic,
    quickQuestions,
    sanitizeChatMessage
} from "../pages/supportChatAnswers";
import {
    buildEnabledResponsibleKeysSet,
    getCrossCategorySuggestions,
    intentMatchesContactKeywords,
    matchIntent,
    normalizeIntentMessage,
    pickBestAttachment,
    pickRandomResponse,
    shouldShowResponsibleButton
} from "../pages/chatbotIntentMatcher";
import "../pages/message.css";
import { toFileUrl } from "../../utils/fileUrl";
import { getOptimizedImage } from "../../components/images/resolveImage";

const STORAGE_KEY = "elakaySupportChatHistoriesV2";
const MIN_MESSAGE_LENGTH = 2;

const iconMap = {
    account: FaUser,
    admin: FaUserShield,
    crown: FaCrown,
    delivery: FaShippingFast,
    info: FaInfoCircle,
    investment: FaSearchDollar,
    job: FaBriefcase,
    legal: FaShieldAlt,
    payment: FaSearchDollar,
    products: FaStore,
    provider: FaUsers,
    security: FaShieldAlt,
    shipping: FaShippingFast,
    shield: FaUserShield,
    start: FaQuestionCircle,
    store: FaStore,
    support: FaHeadset,
    vip: FaCrown
};

const responsibleTopicMap = {
    administrator: "admin",
    elakay: "general",
    products: "product"
};

const createId = (prefix) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const hashStorageValue = (value = "") => {
    let hash = 0;
    String(value).split("").forEach((char) => {
        hash = ((hash << 5) - hash) + char.charCodeAt(0);
        hash |= 0;
    });
    return Math.abs(hash).toString(36);
};

const formatTemplate = (value = "", params = {}) =>
    String(value).replace(/\{([A-Za-z0-9_]+)\}/g, (match, key) =>
        Object.hasOwn(params, key) ? String(params[key] ?? "") : match
    );

const safeJsonParse = (value, fallback) => {
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const normalizeMessagesForSave = (messages = []) =>
    messages
        .filter((message) => !message.typing)
        .map((message) => ({
            sender: message.sender,
            topicId: message.topicId,
            titleKey: message.titleKey,
            textKey: message.textKey,
            text: message.text || "",
            sourceText: message.sourceText || "",
            suggestions: message.suggestions || [],
            provider: message.provider || "local",
            language: message.language || "",
            createdAt: message.createdAt
        }));

const SupportChatShell = (props) => {
    const { infoSource, variant = "page", onBack, onClose } = props;
    const auth = isAuthenticated() || {};
    const token = auth.token;
    const userInfo = auth.user || auth.userInfo || auth;
    const userId = userInfo?._id || userInfo?.id || auth._id || auth.id || "";
    const isChatAuthenticated = Boolean(token);
    const customerRole = userInfo?.userActiveRole?.roleName || "";
    const storageKey = userId
        ? `${STORAGE_KEY}:${userId}`
        : `${STORAGE_KEY}:session-${hashStorageValue(token || "guest")}`;
    const threadRef = useRef(null);

    const pageTexts = useMemo(() => (
        infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.userMessage || {}
    ), [infoSource]);
    const chatTexts = pageTexts.supportChat || {};

    const text = (key, fallback, params = null) => {
        const translated = chatTexts[key];
        const value = translated || elakayAutoText(`support_chat_${key}`, fallback, params);
        return params ? formatTemplate(value, params) : value;
    };

    const topicText = (topicId, field, fallback = "") => {
        const topic = knowledgeById[topicId] || knowledgeById.elakay;
        const key = topic?.[field];
        return key ? text(key, fallback || key) : fallback;
    };

    const makeConversation = () => ({
        clientConversationId: createId("support-chat"),
        topicId: "elakay",
        titleKey: "elakayTitle",
        lastMessage: text("welcomeText", "Ask a question about Elakay."),
        updatedAt: new Date().toISOString(),
        messages: [{
            id: createId("bot"),
            sender: "bot",
            topicId: "elakay",
            titleKey: "welcomeTitle",
            textKey: "welcomeText",
            suggestions: quickQuestions.map((question) => question.key),
            createdAt: new Date().toISOString()
        }]
    });

    const [input, setInput] = useState("");
    const [histories, setHistories] = useState([]);
    const [conversation, setConversation] = useState(makeConversation);
    const [activeTopicId, setActiveTopicId] = useState("elakay");
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const [chatbotIntents, setChatbotIntents] = useState([]);
    // Parametres "Find Responsible Person" (Chatbot Dashboard > Find
    // Responsible Person) : charges une seule fois avec les intents (meme
    // reponse GET /chatbot/intents), voir findResponsiblePersonState plus bas
    // pour la version derivee (Set) consommee par shouldShowResponsibleButton.
    const [findResponsiblePersonSettings, setFindResponsiblePersonSettings] = useState({ enabled: false, messageKeys: [] });
    const [mediaViewer, setMediaViewer] = useState(null); // { type: "image"|"video", url, originalName } | null
    const [menuOpen, setMenuOpen] = useState(false);
    // Widget (modal Contact Us) : la sidebar historique (.support-history-panel)
    // est display:none, donc pas d'acces a l'historique sans ce panneau
    // affiche a la place du fil de discussion (voir menu "..." > "View chat
    // history" et renderHistoryList).
    const [showHistoryPanel, setShowHistoryPanel] = useState(false);
    const [consumedSuggestionIds, setConsumedSuggestionIds] = useState(() => new Set());

    useEffect(() => {
        if (!token) {
            setChatbotIntents([]);
            return;
        }

        const loadChatbotIntents = async () => {
            const data = await listChatbotIntents(token, { language: getElakayLang()?.obj?.code || "en", enabled: true });
            if (data?.success && Array.isArray(data.intents)) {
                setChatbotIntents(data.intents);
            }
            if (data?.success && data.findResponsiblePerson) {
                setFindResponsiblePersonSettings(data.findResponsiblePerson);
            }
        };

        loadChatbotIntents();
    }, [token]);

    useEffect(() => {
        if (!isChatAuthenticated) {
            localStorage.removeItem(STORAGE_KEY);
            setHistories([]);
            return;
        }

        const localHistories = safeJsonParse(localStorage.getItem(storageKey), []);
        if (Array.isArray(localHistories)) setHistories(localHistories);

        const loadRemoteHistories = async () => {
            if (!token) return;
            const data = await getSupportChatHistories(token);
            if (data?.success && Array.isArray(data.histories)) {
                setHistories((current) => {
                    const byClientId = new Map(current.map((item) => [item.clientConversationId || item._id, item]));
                    data.histories.forEach((history) => {
                        const key = history.clientConversationId || history._id;
                        byClientId.set(key, { ...(byClientId.get(key) || {}), ...history });
                    });
                    const merged = Array.from(byClientId.values()).sort((a, b) =>
                        new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
                    );
                    localStorage.setItem(storageKey, JSON.stringify(merged));
                    return merged;
                });
            }
        };

        loadRemoteHistories();
    }, [isChatAuthenticated, storageKey, token]);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [conversation.messages]);

    useEffect(() => {
        if (!menuOpen) return;
        const closeMenu = () => setMenuOpen(false);
        document.addEventListener("click", closeMenu);
        return () => document.removeEventListener("click", closeMenu);
    }, [menuOpen]);

    const persistConversation = async (nextConversation, remote = true) => {
        const summary = {
            ...nextConversation,
            messageCount: normalizeMessagesForSave(nextConversation.messages).length
        };

        setHistories((current) => {
            const filtered = current.filter((item) =>
                (item.clientConversationId || item._id) !== (summary.clientConversationId || summary._id)
            );
            const next = [summary, ...filtered].slice(0, 60);
            localStorage.setItem(storageKey, JSON.stringify(next));
            return next;
        });

        if (!remote || !token) return;

        setSaving(true);
        const data = await saveSupportChatHistory(token, {
            _id: nextConversation._id,
            clientConversationId: nextConversation.clientConversationId,
            topicId: nextConversation.topicId,
            titleKey: nextConversation.titleKey,
            lastMessage: nextConversation.lastMessage,
            messages: normalizeMessagesForSave(nextConversation.messages)
        });
        if (data?.success) {
            setNotice(text("historySaved", elakayAutoText("h_7c1b12f075d8a7", "Conversation saved.")));
        }
        setSaving(false);
    };

    const commitConversation = (nextConversation, remote = true) => {
        setConversation(nextConversation);
        setActiveTopicId(nextConversation.topicId || "elakay");
        persistConversation(nextConversation, remote);
    };

    const currentLanguageCode = () => getElakayLang()?.obj?.code || "en";

    // Derive une seule fois (recalcule uniquement quand les parametres
    // "Find Responsible Person" changent, jamais a chaque render de message)
    // le Set des messageKey actives, pour une recherche O(1) dans
    // shouldShowResponsibleButton (voir chatbotIntentMatcher.js).
    const findResponsiblePersonState = useMemo(() => ({
        enabled: Boolean(findResponsiblePersonSettings.enabled),
        enabledKeysSet: buildEnabledResponsibleKeysSet(findResponsiblePersonSettings.messageKeys)
    }), [findResponsiblePersonSettings]);

    const messageTextForHistory = (message = {}) =>
        message.text || (message.textKey ? text(message.textKey, message.textKey) : "");

    // Suggestions issues de la base de connaissances (Chatbot Dashboard) pour
    // une catégorie/topic donné, utilisées en priorité avant les clés
    // statiques de supportChatAnswers.js — pour que les boutons proposés
    // restent alignés avec le contenu réellement géré par les admins.
    const getDatabaseSuggestionsForTopic = (topicId) => {
        const pool = chatbotIntents
            .filter((item) => item.enabled !== false && item.category === topicId && item.suggestions?.length)
            .flatMap((item) => item.suggestions);
        return Array.from(new Set(pool)).slice(0, 5);
    };

    // Second niveau de suggestions, affiche separement (voir
    // renderCrossCategorySuggestions) : cherche dans toutes les categories,
    // pas seulement celle du topic en cours, priorise par le role du client
    // connecte, trie par recence. Mappe vers la forme legere consommee par
    // handleChainOption (id + title), pas l'intent complet.
    const buildCrossCategorySuggestions = (excludeCategory) =>
        getCrossCategorySuggestions(chatbotIntents, { excludeCategory, customerRole, limit: 3 })
            .map((item) => ({ id: item.id, title: item.title || item.id }));

    const buildBotMessage = (topic, sourceText, extraSuggestions = []) => {
        const dbSuggestions = getDatabaseSuggestionsForTopic(topic.id);
        const suggestions = dbSuggestions.length ? dbSuggestions : (topic.suggestedQuestionKeys || []);
        return {
            id: createId("bot"),
            sender: "bot",
            topicId: topic.id,
            titleKey: topic.titleKey,
            textKey: topic.automaticMessageKey,
            suggestions: Array.from(new Set([...suggestions, ...extraSuggestions])),
            crossCategorySuggestions: buildCrossCategorySuggestions(topic.id),
            sourceText,
            provider: "local",
            language: currentLanguageCode(),
            createdAt: new Date().toISOString()
        };
    };

    const buildGeminiBotMessage = (topic, sourceText, replyText, language) => {
        const dbSuggestions = getDatabaseSuggestionsForTopic(topic.id);
        return {
            id: createId("bot"),
            sender: "bot",
            topicId: topic.id,
            titleKey: topic.titleKey,
            text: replyText,
            suggestions: dbSuggestions.length ? dbSuggestions : (topic.suggestedQuestionKeys || []),
            crossCategorySuggestions: buildCrossCategorySuggestions(topic.id),
            sourceText,
            provider: "gemini",
            language: language || currentLanguageCode(),
            createdAt: new Date().toISOString()
        };
    };

    const buildIntentBotMessage = (intent, topic, sourceText, language) => {
        const chainOptions = (intent.nextIntents || [])
            .map((nextId) => chatbotIntents.find((item) => item.id === nextId))
            .filter(Boolean)
            .map((nextIntent) => ({ id: nextIntent.id, title: nextIntent.title || nextIntent.id }));

        // Quand l'intent a plusieurs images/videos, on ne montre que celle(s)
        // dont les mots-cles propres au fichier correspondent le mieux au
        // message source ; si aucun fichier n'a de mots-cles correspondants,
        // pickBestAttachment renvoie la liste complete inchangee.
        const normalizedSource = normalizeIntentMessage(sourceText);
        const sourceWords = normalizedSource.split(" ").filter(Boolean);

        return {
            id: createId("bot"),
            sender: "bot",
            topicId: topic.id,
            titleKey: topic.titleKey,
            text: pickRandomResponse(intent),
            suggestions: intent.suggestions?.length ? intent.suggestions : (topic.suggestedQuestionKeys || []),
            crossCategorySuggestions: buildCrossCategorySuggestions(intent.category),
            chainOptions,
            images: pickBestAttachment(intent.images || [], normalizedSource, sourceWords),
            videos: pickBestAttachment(intent.videos || [], normalizedSource, sourceWords),
            links: intent.links || [],
            isFinalStep: Boolean(intent.isFinalStep),
            // Voir shouldShowResponsibleButton (chatbotIntentMatcher.js) : ce
            // champ est compare a la liste "Find Responsible Person" du
            // Chatbot Dashboard pour decider d'afficher le bouton "Find a
            // Responsible Person" sous ce message precis.
            messageKey: intent.messageKey || "",
            sourceText,
            provider: "local",
            language: language || currentLanguageCode(),
            createdAt: new Date().toISOString()
        };
    };

    const addUserQuestion = async (rawMessage, options = {}) => {
        const cleanMessage = sanitizeChatMessage(rawMessage);
        if (!cleanMessage || cleanMessage.length < MIN_MESSAGE_LENGTH) return;

        setError("");
        setNotice("");

        // Des qu'une nouvelle question part - clic sur une suggestion, une
        // suggestion croisee, une etape guidee OU simple message tape a la
        // main - les suggestions du DERNIER message bot affiche n'ont plus
        // lieu d'etre : centralise ici (plutot que dans chaque gestionnaire
        // de clic) pour couvrir aussi le cas "message tape a la main", qui
        // ne passait par aucun de ces gestionnaires.
        const lastMessage = conversation.messages[conversation.messages.length - 1];
        if (lastMessage && lastMessage.sender === "bot") {
            markSuggestionsConsumed(lastMessage.id || lastMessage._id);
        }

        if (!isChatAuthenticated) {
            setError(text("authRequiredChat", elakayAutoText("h_f82b83b3f4a5fc", "Please sign in to use the Elakay assistant.")));
            return;
        }

        const language = currentLanguageCode();
        // Priorité à la base de connaissances locale Elakay : on ne sollicite
        // Gemini que si ni les intents du Chatbot Dashboard ni les topics
        // statiques (supportChatAnswers) n'ont de réponse fiable.
        // options.forceIntentId : navigation déterministe dans un parcours
        // guidé (clic sur une étape suivante) — on ne re-matche pas le texte.
        const forcedIntent = options.forceIntentId
            ? chatbotIntents.find((item) => item.id === options.forceIntentId)
            : null;
        const intentMatch = forcedIntent
            ? { intent: forcedIntent, confident: true }
            : options.topicId
                ? { intent: null, confident: false }
                : matchIntent(cleanMessage, chatbotIntents, activeTopicId);
        const topicMatch = options.topicId
            ? { topic: knowledgeById[options.topicId] || knowledgeById.elakay, confident: true }
            : matchSupportTopic(cleanMessage, activeTopicId);
        const detectedTopic = (intentMatch.confident && knowledgeById[intentMatch.intent.category])
            ? knowledgeById[intentMatch.intent.category]
            : topicMatch.topic;
        const shouldContact = Boolean(
            options.requestContact ||
            isContactRequest(cleanMessage) ||
            (intentMatch.confident && intentMatch.intent?.isFinalStep) ||
            (intentMatch.confident && intentMatchesContactKeywords(cleanMessage, intentMatch.intent))
        );

        const userMessage = {
            id: createId("user"),
            sender: "user",
            topicId: detectedTopic.id,
            text: cleanMessage,
            provider: "local",
            language,
            createdAt: new Date().toISOString()
        };
        const typingId = createId("typing");
        const typingMessage = {
            id: typingId,
            sender: "bot",
            topicId: detectedTopic.id,
            typing: true,
            createdAt: new Date().toISOString()
        };

        const withTyping = {
            ...conversation,
            topicId: detectedTopic.id,
            titleKey: detectedTopic.titleKey,
            lastMessage: cleanMessage,
            updatedAt: new Date().toISOString(),
            messages: [...conversation.messages, userMessage, typingMessage]
        };

        setConversation(withTyping);
        setActiveTopicId(detectedTopic.id);

        const finalizeWithMessage = async (botMessage) => {
            // On ne charge plus directement les responsables : on demande
            // d'abord au client de confirmer qu'il veut vraiment contacter
            // quelqu'un (évite un déclenchement trop précoce/accidentel).
            const finalBotMessage = shouldContact
                ? { ...botMessage, needsContactConfirmation: true }
                : botMessage;
            const finalConversation = {
                ...withTyping,
                messages: withTyping.messages.map((message) =>
                    message.id === typingId ? finalBotMessage : message
                ),
                updatedAt: new Date().toISOString()
            };
            commitConversation(finalConversation, true);
        };

        if (intentMatch.confident) {
            if (intentMatch.intent._id) trackChatbotIntentUsage(token, intentMatch.intent._id);
            window.setTimeout(() => finalizeWithMessage(
                buildIntentBotMessage(intentMatch.intent, detectedTopic, cleanMessage, language)
            ), 360);
            return;
        }

        if (topicMatch.confident) {
            window.setTimeout(() => finalizeWithMessage(buildBotMessage(detectedTopic, cleanMessage)), 360);
            return;
        }

        // Aucune réponse locale fiable : on relaie la question à Gemini, qui
        // reste dans le même fil de conversation et le même historique.
        const recentHistory = conversation.messages
            .filter((message) => !message.typing && (message.sender === "user" || message.sender === "bot"))
            .slice(-12)
            .map((message) => ({ sender: message.sender, text: messageTextForHistory(message) }))
            .filter((message) => message.text);

        const aiData = await askSupportAssistant(token, {
            message: cleanMessage,
            topicId: detectedTopic.id,
            language,
            history: recentHistory
        });

        if (aiData?.success && aiData.reply?.text) {
            await finalizeWithMessage(
                buildGeminiBotMessage(detectedTopic, cleanMessage, aiData.reply.text, aiData.reply.language)
            );
            return;
        }

        // Repli si Gemini est indisponible : réponse locale générique, comme
        // aujourd'hui, avec une suggestion de contacter un responsable.
        await finalizeWithMessage(buildBotMessage(detectedTopic, cleanMessage, ["suggestContactSupport"]));
    };

    // Réponse du client à "Voulez-vous contacter un responsable ?". On ne
    // charge la liste des responsables que si le client insiste (confirm=true).
    const handleContactConfirmation = (messageId, topicId, confirmed) => {
        setConversation((current) => {
            const next = {
                ...current,
                messages: current.messages.map((message) =>
                    message.id === messageId
                        // Que ce soit "Yes" ou "No", les suggestions/suggestions
                        // croisees de CE message n'ont plus lieu d'etre une fois
                        // la question repondue (voir renderSuggestions /
                        // renderCrossCategorySuggestions). Le prompt lui-meme
                        // disparait via needsContactConfirmation: false.
                        ? { ...message, needsContactConfirmation: false, contactDeclined: !confirmed, suggestionsHidden: true }
                        : message
                )
            };
            persistConversation(next, false);
            return next;
        });

        if (confirmed) {
            // "Yes, contact someone" : plus de liste a parcourir, on choisit
            // directement le premier responsable disponible et on lance sa
            // demande de contact (voir loadResponsibles { autoContact: true }
            // et handleSendInternalMessage).
            loadResponsibles(messageId, topicId, conversation, { autoContact: true });
        }
    };

    const loadResponsibles = async (messageId, topicId, baseConversation = conversation, options = {}) => {
        const { autoContact = false } = options;
        if (!token) {
            setError(text("authRequired", elakayAutoText("h_c62819acdbe957", "Please sign in to contact a responsible person.")));
            return;
        }

        const setMessageState = (patch) => {
            setConversation((current) => ({
                ...current,
                messages: current.messages.map((message) =>
                    message.id === messageId ? { ...message, ...patch } : message
                )
            }));
        };

        setMessageState({ responsiblesLoading: true, responsiblesError: "", responsibles: [] });

        const responsibleTopic = responsibleTopicMap[topicId] || topicId || "general";
        const data = await getSupportResponsibles(token, responsibleTopic);

        if (data?.success) {
            // "Yes, contact someone" (autoContact) : on ne garde que le
            // premier responsable renvoye, la carte du choix manuel n'est
            // plus affichee (voir handleContactConfirmation) ; on lance
            // ensuite directement sa demande de contact ci-dessous.
            const available = data.responsibles || [];
            const responsibles = autoContact ? available.slice(0, 1) : available;
            const updatedConversation = {
                ...baseConversation,
                messages: baseConversation.messages.map((message) =>
                    message.id === messageId
                        ? {
                            ...message,
                            responsiblesLoading: false,
                            responsibles,
                            fallbackUsed: data.fallbackUsed,
                            responsiblesError: ""
                        }
                        : message
                )
            };
            commitConversation(updatedConversation, false);

            if (autoContact && responsibles[0]) {
                const contactedMessage = updatedConversation.messages.find((message) => message.id === messageId);
                handleSendInternalMessage(responsibles[0], contactedMessage);
            }
            return;
        }

        setMessageState({
            responsiblesLoading: false,
            responsiblesError: data?.error || text("responsiblesError", "Unable to load responsible people.")
        });
    };

    // Point d'entree du bouton automatique "Find a Responsible Person" (voir
    // shouldShowResponsibleButton / renderMessage). Reutilise la procedure
    // deja en place (loadResponsibles + renderResponsibleCards) plutot que
    // d'en batir une nouvelle : seul le declenchement change (automatique,
    // base sur messageKey, au lieu du clic manuel sur le bouton generique).
    const handleFindResponsiblePerson = (message) => {
        const context = {
            conversationId: conversation.clientConversationId || conversation._id,
            chatbotId: message.topicId,
            message,
            messageKey: message.messageKey,
            userId,
            sellerId: userInfo?.sellerId || userInfo?.seller?._id || undefined,
            providerId: userInfo?.providerId || userInfo?.provider?._id || undefined
        };
        loadResponsibles(message.id || message._id, message.topicId);
        return context;
    };

    const submitCurrentMessage = async () => {
        const cleanMessage = sanitizeChatMessage(input);
        if (!cleanMessage || cleanMessage.length < MIN_MESSAGE_LENGTH) return;
        setInput("");
        await addUserQuestion(cleanMessage);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await submitCurrentMessage();
    };

    const handleInputKeyDown = async (event) => {
        if (
            event.key !== "Enter" ||
            event.shiftKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey ||
            event.repeat ||
            event.nativeEvent?.isComposing
        ) {
            return;
        }

        event.preventDefault();
        await submitCurrentMessage();
    };

    const handleSuggestion = (suggestionKey, topicId) => {
        addUserQuestion(text(suggestionKey, suggestionKey), { topicId });
    };

    // Une fois qu'une suggestion (ou une suggestion croisee) d'un message est
    // cliquee, on masque tout le bloc de suggestions de CE message : elles ne
    // sont plus pertinentes une fois la conversation repartie ailleurs.
    const markSuggestionsConsumed = (messageId) => {
        if (!messageId) return;
        setConsumedSuggestionIds((current) => {
            if (current.has(messageId)) return current;
            const next = new Set(current);
            next.add(messageId);
            return next;
        });
    };

    // Clic sur une étape du parcours guidé (chainOptions) : navigation
    // déterministe vers l'intent choisi, pas de nouveau matching par texte.
    const handleChainOption = (option) => {
        addUserQuestion(option.title || option.id, { forceIntentId: option.id });
    };

    const handleNewChat = () => {
        const next = makeConversation();
        setConversation(next);
        setActiveTopicId("elakay");
        setError("");
        setNotice("");
    };

    const restoreHistory = async (history) => {
        setLoadingHistory(true);
        setError("");

        let restored = history;
        if (token && history._id && !Array.isArray(history.messages)) {
            const data = await restoreSupportChatHistory(token, history._id);
            if (data?.success) restored = data.history;
            else setError(data?.error || text("historyRestoreError", elakayAutoText("h_c096d2ca88fe63", "Unable to restore this conversation.")));
        }

        if (Array.isArray(restored.messages)) {
            const normalized = {
                ...restored,
                messages: restored.messages.map((message) => ({
                    ...message,
                    id: message.id || message._id || createId("msg")
                }))
            };
            setConversation(normalized);
            setActiveTopicId(normalized.topicId || "elakay");
        }

        setLoadingHistory(false);
    };

    const removeHistory = async (history, event) => {
        event.stopPropagation();
        const key = history.clientConversationId || history._id;
        const next = histories.filter((item) => (item.clientConversationId || item._id) !== key);
        setHistories(next);
        localStorage.setItem(storageKey, JSON.stringify(next));

        if (token && history._id) await deleteSupportChatHistory(token, history._id);
        if ((conversation.clientConversationId || conversation._id) === key) handleNewChat();
    };

    const removeAllHistories = async () => {
        setHistories([]);
        localStorage.removeItem(storageKey);
        handleNewChat();
        if (token) await deleteAllSupportChatHistories(token);
    };

    // Met à jour le statut de la demande de contact pour CE responsable
    // précis sur le message (plusieurs cartes responsable peuvent coexister
    // sur un même message bot, chacune avec sa propre demande).
    const setContactRequestState = (messageId, responsibleId, patch) => {
        setConversation((current) => {
            const next = {
                ...current,
                messages: current.messages.map((message) =>
                    (message.id || message._id) === messageId
                        ? {
                            ...message,
                            contactRequests: {
                                ...(message.contactRequests || {}),
                                [responsibleId]: { ...(message.contactRequests?.[responsibleId] || {}), ...patch }
                            }
                        }
                        : message
                )
            };
            persistConversation(next, false);
            return next;
        });
    };

    // Le client n'écrit plus directement au responsable : une demande
    // "pending" est créée, et le responsable doit l'accepter avant qu'un
    // vrai message ne soit envoyé (voir page "Demandes de contact").
    const handleSendInternalMessage = async (responsible, botMessage) => {
        if (!responsible?._id || !token) {
            setError(text("authRequired", elakayAutoText("h_c62819acdbe957", "Please sign in to contact a responsible person.")));
            return;
        }

        const messageId = botMessage.id || botMessage._id;
        const topicTitle = topicText(botMessage.topicId, "titleKey", "Elakay support");

        setContactRequestState(messageId, responsible._id, { status: "sending" });

        const data = await createContactRequest(token, {
            responsibleId: responsible._id,
            topic: botMessage.topicId,
            topicLabel: topicTitle,
            question: botMessage.sourceText || topicTitle
        });

        if (data?.success) {
            setContactRequestState(messageId, responsible._id, {
                status: data.request?.status || "pending",
                requestId: data.request?._id
            });
            setNotice(text("contactRequestSent", elakayAutoText("h_d6099d679c7338", "Request sent. Waiting for {name} to accept."), { name: responsible.publicId || elakayAutoText("h_f32d5a3b17e614", "Support") }));
        } else {
            setContactRequestState(messageId, responsible._id, { status: "error" });
            setError(data?.error || text("directMessageError", elakayAutoText("h_c0944efb86746b", "Message not sent.")));
        }
    };

    const handleCheckContactRequestStatus = async (messageId, responsible) => {
        const requestId = conversation.messages
            .find((message) => (message.id || message._id) === messageId)
            ?.contactRequests?.[responsible._id]?.requestId;
        if (!requestId || !token) return;

        const data = await listMyContactRequests(token);
        if (!data?.success) return;

        const updated = data.requests?.find((request) => String(request._id) === String(requestId));
        if (updated) {
            setContactRequestState(messageId, responsible._id, { status: updated.status });
            if (updated.status === "accepted") {
                setNotice(text("contactRequestAccepted", elakayAutoText("h_fda1003b15e4d7", "{name} accepted your request. Your message has been sent."), { name: responsible.publicId || elakayAutoText("h_f32d5a3b17e614", "Support") }));
            } else if (updated.status === "declined") {
                setNotice(text("contactRequestDeclined", elakayAutoText("h_1569bbe1b9fcdf", "{name} is not available right now. You can try another responsible person."), { name: responsible.publicId || elakayAutoText("h_f32d5a3b17e614", "Support") }));
            }
        }
    };

    const renderIcon = (iconName) => {
        const Icon = iconMap[iconName] || FaInfoCircle;
        return <Icon />;
    };

    const renderSuggestions = (message) => {
        const messageId = message.id || message._id;
        if (consumedSuggestionIds.has(messageId) || message.suggestionsHidden) return null;
        if (!Array.isArray(message.suggestions) || message.suggestions.length === 0) return null;
        return (
            <div className="support-chat-suggestions">
                {message.suggestions.map((suggestionKey) => (
                    <button
                        type="button"
                        className="support-chat-suggestion"
                        key={suggestionKey}
                        onClick={() => handleSuggestion(suggestionKey, message.topicId)}
                    >
                        {text(suggestionKey, suggestionKey)}
                    </button>
                ))}
            </div>
        );
    };

    // Deuxieme groupe de suggestions, visuellement distinct des suggestions
    // de l'intent matche : intents d'autres categories, pertinents pour le
    // role du client connecte et/ou recents (voir buildCrossCategorySuggestions).
    // Reutilise handleChainOption pour la navigation (meme comportement
    // qu'un clic sur une etape de parcours guide : pas de re-matching texte).
    const renderCrossCategorySuggestions = (message) => {
        const messageId = message.id || message._id;
        if (consumedSuggestionIds.has(messageId) || message.suggestionsHidden) return null;
        if (!Array.isArray(message.crossCategorySuggestions) || message.crossCategorySuggestions.length === 0) {
            return null;
        }
        return (
            <div className="support-chat-suggestions support-chat-suggestions-cross">
                <span className="support-chat-suggestions-cross-label">
                    <FaLightbulb />
                    <span>{text("crossCategorySuggestionsLabel", "You might also be interested in")}</span>
                </span>
                {message.crossCategorySuggestions.map((option) => (
                    <button
                        type="button"
                        className="support-chat-suggestion support-chat-suggestion-cross"
                        key={option.id}
                        onClick={() => handleChainOption(option)}
                    >
                        {option.title}
                    </button>
                ))}
            </div>
        );
    };

    // Meme position que renderSuggestions/renderCrossCategorySuggestions :
    // rendu comme sa propre rangee "user" (alignee a droite) sous le message
    // bot, plutot qu'a l'interieur de sa bulle (voir la pile
    // support-chat-suggestions-stack dans la boucle des messages).
    const renderContactConfirm = (message) => {
        if (!message.needsContactConfirmation) return null;
        if (consumedSuggestionIds.has(message.id || message._id)) return null;
        return (
            <div className="support-contact-confirm">
                <p>{text("contactConfirmPrompt", "Do you want to contact a responsible person about this?")}</p>
                <div className="support-contact-confirm-actions">
                    <button
                        type="button"
                        className="support-card-action primary"
                        onClick={() => handleContactConfirmation(message.id || message._id, message.topicId, true)}
                    >
                        {text("contactConfirmYes", "Yes, contact someone")}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleContactConfirmation(message.id || message._id, message.topicId, false)}
                    >
                        {text("contactConfirmNo", "No, continue chatting")}
                    </button>
                </div>
            </div>
        );
    };

    const renderChainOptions = (message) => {
        if (!Array.isArray(message.chainOptions) || message.chainOptions.length === 0) return null;
        return (
            <div className="support-chain-options">
                {message.chainOptions.map((option) => (
                    <button type="button" key={option.id} onClick={() => handleChainOption(option)}>
                        <span>{option.title}</span>
                    </button>
                ))}
            </div>
        );
    };

    // Images/vidéos en petit aperçu directement dans la bulle (clic -> modal
    // de lecture en grand) ; liens cliquables ouverts dans un nouvel onglet.
    const renderAttachments = (message) => {
        const images = message.images || [];
        const videos = message.videos || [];
        const links = message.links || [];
        if (!images.length && !videos.length && !links.length) return null;

        return (
            <div className="support-attachments">
                {images.map((image, index) => (
                    <button
                        type="button"
                        key={image.url || index}
                        className="support-attachment-thumb"
                        onClick={() => setMediaViewer({ type: "image", url: image.url, originalName: image.originalName })}
                    >
                        <img src={getOptimizedImage(toFileUrl(image.url), { preset: "content" })} alt={image.originalName || elakayAutoText("h_0e76292794888d", "image")} />
                    </button>
                ))}
                {videos.map((video, index) => (
                    <button
                        type="button"
                        key={video.url || index}
                        className="support-attachment-thumb support-attachment-video"
                        onClick={() => setMediaViewer({ type: "video", url: video.url, originalName: video.originalName })}
                    >
                        <video src={toFileUrl(video.url)} muted />
                        <FaPlayCircle className="support-attachment-play" />
                    </button>
                ))}
                {links.map((link, index) => (
                    <a
                        key={link.url || index}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="support-attachment-link"
                    >
                        <FaExternalLinkAlt /> <span>{link.label || link.url}</span>
                    </a>
                ))}
            </div>
        );
    };

    const renderResponsibleCards = (message) => {
        if (consumedSuggestionIds.has(message.id || message._id)) return null;
        if (message.responsiblesLoading) {
            return (
                <div className="support-responsibles-state">
                    <FaSyncAlt className="message-spin" />
                    <span>{text("responsiblesLoading", "Looking for responsible people...")}</span>
                </div>
            );
        }

        if (message.responsiblesError) {
            return (
                <div className="support-responsibles-state error">
                    <FaExclamationCircle />
                    <span>{message.responsiblesError}</span>
                </div>
            );
        }

        if (!Array.isArray(message.responsibles)) return null;

        if (message.responsibles.length === 0) {
            return (
                <div className="support-responsibles-state">
                    <FaHeadset />
                    <span>{text("responsiblesEmpty", "No responsible person is available for this topic yet.")}</span>
                </div>
            );
        }

        return (
            <div className="support-responsible-list">
                {message.fallbackUsed && (
                    <div className="support-responsibles-note">
                        {text("fallbackUsed", "No specific responsible person was found. General support is shown instead.")}
                    </div>
                )}
                {message.responsibles.map((responsible) => {
                    const requestState = message.contactRequests?.[responsible._id];
                    const messageId = message.id || message._id;
                    return (
                        <article className="support-responsible-card" key={responsible._id}>
                            <div className="support-responsible-avatar">
                                <span>{responsible.publicId || "SUP"}</span>
                            </div>
                            <div className="support-responsible-info">
                                <h3>{responsible.label || text("responsibleLabel", "Responsible: {id}", { id: responsible.publicId || "SUP" })}</h3>
                                <p>{responsible.userActiveRole?.roleName || text("responsibleRole", "Responsible")}</p>
                                <small>{text(`status_${responsible.status || "available"}`, "Available")}</small>
                            </div>
                            <div className="support-responsible-actions">
                                {!requestState && (
                                    <button
                                        type="button"
                                        className="support-card-action primary"
                                        onClick={() => handleSendInternalMessage(responsible, message)}
                                    >
                                        <FaPaperPlane />
                                        <span>{text("contactInsideElakay", "Contact in Elakay")}</span>
                                    </button>
                                )}
                                {requestState?.status === "sending" && (
                                    <span className="support-contact-request-status">
                                        <FaSyncAlt className="message-spin" /> {text("contactRequestSending", "Sending request...")}
                                    </span>
                                )}
                                {requestState?.status === "pending" && (
                                    <div className="support-contact-request-status pending">
                                        <span>{text("contactRequestPending", "Pending acceptance...")}</span>
                                        <button type="button" onClick={() => handleCheckContactRequestStatus(messageId, responsible)}>
                                            <FaSyncAlt /> {text("contactRequestCheck", "Check status")}
                                        </button>
                                    </div>
                                )}
                                {requestState?.status === "accepted" && (
                                    <span className="support-contact-request-status accepted">
                                        {text("contactRequestAcceptedShort", "Accepted — message sent")}
                                    </span>
                                )}
                                {requestState?.status === "declined" && (
                                    <span className="support-contact-request-status declined">
                                        {text("contactRequestDeclinedShort", "Not available")}
                                    </span>
                                )}
                                {requestState?.status === "error" && (
                                    <button type="button" className="support-card-action primary" onClick={() => handleSendInternalMessage(responsible, message)}>
                                        <FaPaperPlane /> <span>{text("retry", "Retry")}</span>
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>
        );
    };

    const renderMessage = (message) => {
        const mine = message.sender === "user";
        const topic = knowledgeById[message.topicId] || knowledgeById.elakay;
        const title = message.titleKey ? text(message.titleKey, message.titleKey) : topicText(message.topicId, "titleKey");
        const body = message.text || (message.textKey ? text(message.textKey, message.textKey) : "");
        // Bouton automatique (voir handleFindResponsiblePerson) : ne s'affiche
        // jamais sous un message utilisateur, uniquement quand le messageKey
        // de CE message bot est present et active dans "Find Responsible
        // Person" (Chatbot Dashboard).
        const showAutoResponsibleButton = !mine && shouldShowResponsibleButton(message.messageKey, findResponsiblePersonState);

        return (
            <article key={message.id || message._id} className={`support-chat-message ${mine ? "user" : "bot"}`}>
                {!mine && <div className="support-message-icon">{renderIcon(topic.icon)}</div>}
                {mine && <div className="support-message-avatar"><FaUser /></div>}
                <div className="support-message-body">
                    {message.typing ? (
                        <div className="support-typing" aria-label={text("typing", "Assistant is typing")}>
                            <span></span><span></span><span></span>
                        </div>
                    ) : (
                        <>
                            {!mine && title && <h3>{title}</h3>}
                            <p>
                                {body}
                                {mine && message.createdAt && (
                                    <span className="support-message-time">{formatTime(message.createdAt)}</span>
                                )}
                            </p>
                            {showAutoResponsibleButton && (
                                <button
                                    type="button"
                                    className="support-find-responsible-btn"
                                    onClick={() => handleFindResponsiblePerson(message)}
                                    disabled={message.responsiblesLoading}
                                >
                                    <FaUser />
                                    <span>{text("findResponsiblePerson", "Find a Responsible Person")}</span>
                                </button>
                            )}
                            {!mine && renderAttachments(message)}
                            {!mine && renderChainOptions(message)}
                            {!mine && message.isFinalStep && (
                                <p className="support-final-step-hint">
                                    {text("finalStepHint", "This is the end of this guided path. If you still need help, you can contact a responsible person below.")}
                                </p>
                            )}
                            {/* {!mine && !message.needsContactConfirmation && !showAutoResponsibleButton && (
                                <div className="support-message-tools">
                                    <button
                                        type="button"
                                        onClick={() => loadResponsibles(message.id || message._id, message.topicId)}
                                        disabled={message.responsiblesLoading}
                                    >
                                        <FaHeadset />
                                        <span>{text("findResponsibleButton", "Find a responsible person")}</span>
                                    </button>
                                </div>
                            )} */}
                        </>
                    )}
                </div>
            </article>
        );
    };

    const historyTitle = (history) => {
        const topic = knowledgeById[history.topicId] || knowledgeById.elakay;
        return text(history.titleKey || topic.titleKey, topic.titleKey);
    };

    const formatDate = (value) => {
        if (!value) return "";
        return new Date(value).toLocaleString();
    };

    // Partagee entre la sidebar historique (variant="page") et le panneau
    // affiche a la place du fil de discussion en variant="widget" (voir
    // showHistoryPanel), pour ne pas dupliquer ce balisage deux fois.
    const renderHistoryList = () => (
        <div className="support-history-list">
            {loadingHistory && (
                <div className="support-history-empty">
                    <FaSyncAlt className="message-spin" />
                    <span>{text("historyLoading", "Loading conversation...")}</span>
                </div>
            )}
            {!loadingHistory && histories.length === 0 && (
                <div className="support-history-empty">
                    <FaHistory />
                    <span>{text("historyEmpty", "No conversation history yet.")}</span>
                </div>
            )}
            {!loadingHistory && histories.map((history) => {
                const topic = knowledgeById[history.topicId] || knowledgeById.elakay;
                const active = (history.clientConversationId || history._id) === (conversation.clientConversationId || conversation._id);
                return (
                    <button
                        type="button"
                        key={history.clientConversationId || history._id}
                        className={`support-history-item ${active ? "active" : ""}`}
                        onClick={() => {
                            restoreHistory(history);
                            setShowHistoryPanel(false);
                        }}
                    >
                        <span className="support-history-icon">{renderIcon(topic.icon)}</span>
                        <span className="support-history-main">
                            <strong>{historyTitle(history)}</strong>
                            <small>{history.lastMessage || text("historyNoMessage", "No message")}</small>
                            <time>{formatDate(history.updatedAt)}</time>
                        </span>
                        <span
                            role="button"
                            tabIndex={0}
                            className="support-history-delete"
                            onClick={(event) => removeHistory(history, event)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") removeHistory(history, event);
                            }}
                            aria-label={text("deleteChat", "Delete chat")}
                        >
                            <FaTrash />
                        </span>
                    </button>
                );
            })}
        </div>
    );

    const formatTime = (value) => {
        if (!value) return "";
        return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const isSameDay = (a, b) =>
        Boolean(a) && Boolean(b) && new Date(a).toDateString() === new Date(b).toDateString();

    const formatDayLabel = (value) => {
        if (!value) return "";
        const date = new Date(value);
        const sameYear = date.getFullYear() === new Date().getFullYear();
        return date.toLocaleDateString([], sameYear
            ? { month: "long", day: "numeric" }
            : { month: "long", day: "numeric", year: "numeric" });
    };

    const goToSignin = () => {
        window.location.href = "/signin";
    };

    const handleBack = onBack || onClose || (() => window.history.back());

    return (
        <>
            {!isChatAuthenticated ? (
                <div className={`support-chat-page support-chat-page-auth ${variant === "widget" ? "support-chat-page-widget" : ""}`}>
                    <section className="support-chat-shell support-auth-shell">
                        <div className="support-auth-required">
                            <div className="support-auth-icon">
                                <FaUserShield />
                            </div>
                            <h2>{text("authRequiredTitle", "Sign in required")}</h2>
                            <p>{text("authRequiredChat", "Please sign in to use the Elakay assistant. Every support conversation is linked to your customer account.")}</p>
                            <button type="button" onClick={goToSignin}>
                                <FaUser />
                                <span>{text("signinButton", "Sign in")}</span>
                            </button>
                        </div>
                    </section>
                </div>
            ) : (
            <div className={`support-chat-page ${variant === "widget" ? "support-chat-page-widget" : ""}`}>
                <aside className="support-history-panel">
                    <div className="support-history-actions">
                        <button type="button" onClick={handleNewChat}>
                            <FaPlus />
                            <span>{text("newChat", "New chat")}</span>
                        </button>
                        <button type="button" onClick={removeAllHistories} disabled={!histories.length}>
                            <FaTrash />
                            <span>{text("deleteAllChats", "Delete all")}</span>
                        </button>
                    </div>

                    {renderHistoryList()}
                </aside>

                <section className="support-chat-shell">
                    <header className="support-chat-header">
                        <button
                            type="button"
                            className="support-chat-header-back"
                            onClick={showHistoryPanel ? () => setShowHistoryPanel(false) : handleBack}
                            aria-label={showHistoryPanel ? text("backToChat", "Back to chat") : text("back", "Back")}
                        >
                            <FaArrowLeft />
                        </button>

                        <div className="support-chat-header-identity">
                            <span className="support-chat-header-avatar">
                                <FaRobot />
                                <span className="support-chat-header-dot" />
                            </span>
                            <div className="support-chat-header-text">
                                <h2>{text("assistantTitle", "Elakay Assistant")}</h2>
                                <p>
                                    {showHistoryPanel
                                        ? text("historyPanelSubtitle", "Your past conversations")
                                        : (saving ? text("saving", "Saving...") : topicText(activeTopicId, "shortDescriptionKey", text("assistantSubtitle", "Official Elakay virtual assistant.")))}
                                </p>
                            </div>
                        </div>

                        <div className="support-chat-header-actions">
                            <div className="support-chat-header-menu">
                                <button
                                    type="button"
                                    className="support-chat-header-icon-btn"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setMenuOpen((open) => !open);
                                    }}
                                    aria-label={text("moreOptions", "More options")}
                                >
                                    <FaEllipsisH />
                                </button>
                                {menuOpen && (
                                    <div className="support-chat-header-dropdown">
                                        {variant === "widget" && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    setShowHistoryPanel(true);
                                                }}
                                            >
                                                <FaHistory />
                                                <span>{text("viewChatHistory", "View chat history")}</span>
                                            </button>
                                        )}
                                        <button type="button" onClick={handleNewChat}>
                                            <FaPlus />
                                            <span>{text("newChat", "New chat")}</span>
                                        </button>
                                        <button type="button" onClick={removeAllHistories} disabled={!histories.length}>
                                            <FaTrash />
                                            <span>{text("deleteAllChats", "Delete all")}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            {onClose && (
                                <button
                                    type="button"
                                    className="support-chat-header-icon-btn"
                                    onClick={onClose}
                                    aria-label={text("close", "Close")}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </header>

                    {showHistoryPanel ? (
                        <div className="support-chat-history-inline">
                            <div className="support-history-actions">
                                <button type="button" onClick={() => { handleNewChat(); setShowHistoryPanel(false); }}>
                                    <FaPlus />
                                    <span>{text("newChat", "New chat")}</span>
                                </button>
                                <button type="button" onClick={removeAllHistories} disabled={!histories.length}>
                                    <FaTrash />
                                    <span>{text("deleteAllChats", "Delete all")}</span>
                                </button>
                            </div>
                            {renderHistoryList()}
                        </div>
                    ) : (
                        <>
                            {(notice || error) && (
                                <div className={`support-chat-alert ${error ? "error" : "success"}`}>
                                    {error || notice}
                                </div>
                            )}

                            <div className="support-chat-body" ref={threadRef}>
                                {conversation.messages.length === 0 ? (
                                    <div className="support-empty-state">
                                        <FaRobot />
                                        <p>{text("emptyChat", "Start a new conversation with the Elakay assistant.")}</p>
                                    </div>
                                ) : (
                                    conversation.messages.map((message, index) => {
                                        const previousMessage = conversation.messages[index - 1];
                                        const showDateDivider = Boolean(message.createdAt) && !isSameDay(message.createdAt, previousMessage?.createdAt);
                                        const mine = message.sender === "user";
                                        const suggestionsNode = !mine ? renderSuggestions(message) : null;
                                        const crossSuggestionsNode = !mine ? renderCrossCategorySuggestions(message) : null;
                                        const contactConfirmNode = !mine ? renderContactConfirm(message) : null;
                                        const responsibleCardsNode = !mine ? renderResponsibleCards(message) : null;
                                        return (
                                            <React.Fragment key={message.id || message._id || `msg-${index}`}>
                                                {showDateDivider && (
                                                    <div className="support-chat-date-divider">
                                                        <span>{formatDayLabel(message.createdAt)}</span>
                                                    </div>
                                                )}
                                                {renderMessage(message)}
                                                {/* Suggestions/confirmation/responsables positionnes comme les
                                                    messages de l'utilisateur (alignes a droite), plutot qu'a
                                                    l'interieur de la bulle du bot. */}
                                                {(suggestionsNode || crossSuggestionsNode || contactConfirmNode || responsibleCardsNode) && (
                                                    <div className="support-chat-message user">
                                                        <div className="support-chat-suggestions-stack">
                                                            {suggestionsNode}
                                                            {crossSuggestionsNode}
                                                            {contactConfirmNode}
                                                            {responsibleCardsNode}
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </div>

                            <form className="support-chat-input-bar" onSubmit={handleSubmit}>
                                <textarea
                                    value={input}
                                    onChange={(event) => setInput(event.target.value)}
                                    onKeyDown={handleInputKeyDown}
                                    placeholder={text("inputPlaceholder", "Message...")}
                                    maxLength={800}
                                    rows={1}
                                />
                                <button
                                    type="submit"
                                    className="support-chat-send-btn"
                                    disabled={sanitizeChatMessage(input).length < MIN_MESSAGE_LENGTH}
                                    aria-label={text("askButton", "Send")}
                                >
                                    <FaPaperPlane />
                                </button>
                            </form>
                        </>
                    )}
                </section>
            </div>
            )}

            <Modal show={Boolean(mediaViewer)} onHide={() => setMediaViewer(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{mediaViewer?.originalName || text("mediaViewerTitle", "Aperçu")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="support-media-modal-body">
                    {mediaViewer?.type === "image" && <img src={toFileUrl(mediaViewer.url)} alt={mediaViewer.originalName || elakayAutoText("h_0e76292794888d", "image")} />}
                    {mediaViewer?.type === "video" && <video src={toFileUrl(mediaViewer?.url)} controls autoPlay />}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SupportChatShell;
